import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { Layout } from 'antd';
import { useParams, useHistory } from 'react-router-dom';
import useGlobal from '../../../store/Store';
import { getTestFile } from '../../../lib/api/aws-api';
import { getMultiflowStatus } from '../../../lib/api/flow-api';
import { getModel } from '../../../lib/api/algorithm-api';
import { PdfContainer } from '../../../common/pdf/PdfContainer';
import ResultSideBar from './ResultSideBar';
import { processInferenceResult } from '../../../lib/util';
import { isFinished } from '../../../lib/util/status';
import { reformInferenceToPdfFile } from '../../../lib/util/reform';
import api from '../../../config/AxiosService';
import { InferenceFile, PdfFile } from '../../../types';
import { MultiflowStatus } from '../../../types/flow';
import { TopBar } from '../../../components/layout/TopBar';
import { getResultLabels } from '../../../lib/labels';
import { DetailContainer } from '../../../components/layout/DetailContainer';

const { Content } = Layout;

const InferenceResult = () => {
  let history = useHistory();

  const { fileId, modelId, type } = useParams() as {
    fileId: string;
    modelId: string;
    type: string;
  };
  const [inferenceFile, setInferenceFile] = useState<null | InferenceFile>(null);
  const [pdfFile, setPdfFile] = useState<PdfFile>();
  const [globalState, globalActions] = useGlobal();
  const [prevId, setPrevId] = useState<null | string>(null);
  const [nextId, setNextId] = useState<null | string>(null);
  const [multiflowStatus, setMultiflowStatus] = useState<null | MultiflowStatus>(null);

  const reset = useCallback(() => {
    globalActions.setLabelOptions(null);
    globalActions.setLabelEdit(null);
    globalActions.removeLabels();
    setInferenceFile(null);
  }, [globalActions, setInferenceFile]);

  useEffect(() => {
    const fetchData = async () => {
      const m = await getModel(modelId);

      const annotationTypes = m.algorithm && m.algorithm.info && m.algorithm.info.annotationTypes;
      globalActions.setLabelOptions(annotationTypes);

      const inferenceFile = await getTestFile(fileId);

      if (m.algorithm.multiflow && inferenceFile.run && inferenceFile.run.id) {
        try {
          const multiflowStatus: MultiflowStatus = await getMultiflowStatus(inferenceFile.run.id);
          setMultiflowStatus(multiflowStatus);
        } catch (error) {
          console.error('Could not fetch status.');
        }
      }

      const reformedFile = processInferenceResult(inferenceFile);

      console.log('reformed', reformedFile);

      setInferenceFile(reformedFile);
      setPdfFile(reformInferenceToPdfFile(reformedFile));

      const labels = getResultLabels(reformedFile, reformedFile.model);
      globalActions.setLabels(labels.filter(l => !!l));

      const response = await api.get<{ items: InferenceFile[] }>(
        `/flows/inference-list/${inferenceFile.flowId}?page=${1}&size=${1000}`
      );

      const index = response.data.items.findIndex(it => it.id === inferenceFile.id);
      let prevId = null;
      let nextId = null;
      if (index > 0) {
        for (var i = index - 1; i > -1; i--) {
          if (isFinished(response.data.items[i])) {
            prevId = response.data.items[i].id;
            break;
          }
        }
      }
      if (index < response.data.items.length - 1) {
        for (let i = index + 1; i < response.data.items.length; i++) {
          if (isFinished(response.data.items[i])) {
            nextId = response.data.items[i].id;
            break;
          }
        }
      }

      setPrevId(prevId);
      setNextId(nextId);
      return inferenceFile;
    };

    reset();
    fetchData();
  }, [fileId, globalActions, modelId, reset]);

  const keyPress = useCallback(
    (event: any): void => {
      if (inferenceFile) {
        if (event.code === 'ArrowRight' && nextId) {
          history.push(`/${type}/${inferenceFile.flowId}/model/${inferenceFile.model.id}/${nextId}`);
        }
        if (event.code === 'ArrowLeft' && prevId) {
          history.push(`/${type}/${inferenceFile.flowId}/model/${inferenceFile.model.id}/${prevId}`);
        }
      }
    },
    [inferenceFile, history, nextId, prevId, type]
  );

  useEffect(() => {
    window.addEventListener('keyup', keyPress);
    return () => {
      window.removeEventListener('keyup', keyPress);
    };
  }, [keyPress]);

  const { labels } = globalState;
  const filteredLabels = useMemo(() => labels.filter(label => label.exists || !label.reliable), [labels]);

  return (
    <>
      <TopBar
        title={(inferenceFile && inferenceFile.fileName) || ''}
        closeFunction={() => {
          inferenceFile &&
            inferenceFile.model &&
            history.push(`/${type}/${inferenceFile.model.algorithm.id}/${inferenceFile.flowId}`);
        }}
        prevId={prevId}
        nextId={nextId}
        baseUrl={`/${type}/${inferenceFile?.flowId}/model/${inferenceFile?.model.id}`}
      />
      <DetailContainer>
        <Layout>
          <Content>
            <div className={'pdf-layout'} style={{ width: '100%' }}>
              <PdfContainer
                file={pdfFile}
                labels={filteredLabels}
                showNonExisting={true}
                onAnnotationChange={() => {}}
              />
            </div>
          </Content>
        </Layout>
        <ResultSideBar multiflowStatus={multiflowStatus} inferenceFile={inferenceFile} />
      </DetailContainer>
    </>
  );
};

export default InferenceResult;
